import { useAuth } from '@hb/react'
import { DOT_PHRASE_EXPANSION_LOG, DotPhraseTemplate, USERS_ADMIN, WithId } from '@hb/shared'
import { addDoc, collection, doc, updateDoc } from 'firebase/firestore'
import { useCallback, useMemo, useRef, useState } from 'react'
import { db } from '../../../backend/db/db'
import { useDocument } from '../../../backend/db/hooks/useDocument'
import { useDotPhraseTemplates } from './useDotPhraseTemplates'

interface AdminUserDotPhrase {
  dotPhraseNoticeShown?: boolean
}

export type TryExpandResult =
  | { matched: true; newValue: string; newCursor: number }
  | { matched: false }

export interface UseDotPhraseExpansion {
  tryExpand: (value: string, selectionStart: number, fieldName: string) => TryExpandResult
  showNotice: boolean
  dismissNotice: () => Promise<void>
  templates: WithId<DotPhraseTemplate>[]
}

export const useDotPhraseExpansion = (
  practiceId: string | null | undefined,
): UseDotPhraseExpansion => {
  const templates = useDotPhraseTemplates(practiceId)
  const currentUserId = useAuth(s => s.authUser?.uid)
  const { data: userAdminDoc } = useDocument<AdminUserDotPhrase>(USERS_ADMIN, currentUserId ?? null)
  const [showNotice, setShowNotice] = useState(false)

  // tryExpand only runs on Tab — it does not need to be recreated when its inputs change.
  // Keeping its identity stable (and the returned context value memoized below) is critical:
  // every text input in the form consumes this context, so a new value reference would
  // re-render all of them at once whenever templates / the user doc / the notice flag update.
  const templatesRef = useRef(templates)
  templatesRef.current = templates
  const userIdRef = useRef(currentUserId)
  userIdRef.current = currentUserId
  const noticeShownRef = useRef(userAdminDoc?.dotPhraseNoticeShown)
  noticeShownRef.current = userAdminDoc?.dotPhraseNoticeShown

  const tryExpand = useCallback(
    (value: string, selectionStart: number, fieldName: string): TryExpandResult => {
      const before = value.slice(0, selectionStart)
      const wordMatch = /(\S+)$/.exec(before)
      const keyword = wordMatch?.[1]
      if (!keyword) return { matched: false }

      const template = templatesRef.current.find(t => t.keyword === keyword)
      if (!template) return { matched: false }

      const after = value.slice(selectionStart)
      const newValue = before.slice(0, before.length - keyword.length) + template.body + after
      const newCursor = before.length - keyword.length + template.body.length

      const userId = userIdRef.current
      if (userId) {
        addDoc(collection(db, DOT_PHRASE_EXPANSION_LOG), {
          userId,
          keyword,
          fieldPath: fieldName,
          templateLevel: template.level,
          expandedAt: Date.now(),
          fromTemplate: true,
        }).catch(console.error)
      }

      if (!noticeShownRef.current) {
        setShowNotice(true)
      }

      return { matched: true, newValue, newCursor }
    },
    [],
  )

  const dismissNotice = useCallback(async () => {
    setShowNotice(false)
    const userId = userIdRef.current
    if (!userId) return
    await updateDoc(doc(db, USERS_ADMIN, userId), {
      dotPhraseNoticeShown: true,
    }).catch(console.error)
  }, [])

  // Memoize so the value reference only changes when something a consumer actually
  // reads changes (templates for the picker, showNotice for the modal).
  return useMemo<UseDotPhraseExpansion>(
    () => ({ tryExpand, showNotice, dismissNotice, templates }),
    [tryExpand, showNotice, dismissNotice, templates],
  )
}
